// The template editor's single form: its values, and the two mappings that
// hang off them — from a saved template, and to a PATCH body. Kept out of
// the components so the rules stay readable on their own.
//
// An emptied field is CLEARED on the wire: `toBody` sends an explicit null
// for it, which the API stores as null.
//
// No live preview lives in this file. Unlike ewt's template — which was a
// whole signage screen's look and copy, previewable on its own — a feedback
// template is only ever seen through a survey, so the live preview (Task 9)
// composes off the survey's own form state instead of this one.

import { z } from "zod";
import type { TemplateBody } from "@/api/templates";
import type { Template } from "@/api/types";
import type { DraftTemplate } from "@/features/preview/draft";
import { isPristineCss, TEMPLATE_CSS_SCAFFOLD } from "@/features/theming/css-scaffold";

const HEX = /^#[0-9a-fA-F]{6}$/;

// Curated font stacks (label -> full CSS stack). "custom" reveals free text.
// Ported from ewt as-is, where the families named here were bundled and
// self-served by the display page, with a font-parity test pinning the two
// in lockstep. The widget bundles no fonts of its own (frontend/widget's
// design.ts: "a custom face rides the contract [fontStack/fontFaceUrl]
// instead of a bundled default") and carries no such parity test, so these
// six labels are, today, just curated starting stacks — a kiosk without the
// named family installed renders its generic fallback, same as "custom".
//
// The fallback behind each is for the window between first paint and font load,
// not a substitute: if the woff2 fails to fetch, the sign still renders.
// Alphabetical by label: the picker renders in insertion order, and a list of
// typefaces has no meaningful order other than the one someone can scan.
export const FONT_STACKS: Record<string, string> = {
    caveat: "Caveat, cursive",
    "google-sans": '"Google Sans", system-ui, sans-serif',
    inter: "Inter, system-ui, sans-serif",
    "open-sans": '"Open Sans", system-ui, sans-serif',
    roboto: "Roboto, system-ui, sans-serif",
    "roboto-mono": '"Roboto Mono", ui-monospace, monospace',
};

// Every template names its typeface. There is deliberately no "display
// default" row: it stored a null stack, which the display page substitutes its
// own default for — and now that the default is the bundled Inter, that row
// rendered identically to picking Inter while implying something else was
// happening. `DEFAULT_FONT_STACK` in the display still matters, but for the case
// no picker can cover: a screen with no template at all.
export const FONT_LABELS: Record<string, string> = {
    // Families alphabetically, free text last.
    caveat: "Caveat",
    "google-sans": "Google Sans",
    inter: "Inter",
    "open-sans": "Open Sans",
    roboto: "Roboto",
    "roboto-mono": "Roboto Mono",
    // A template saved before these families existed keeps its stack and shows
    // up here rather than being silently rewritten.
    custom: "Custom…",
};

export function fontChoiceFor(stack: string | null): string {
    // A null stack means the display substitutes its own default, which is
    // Inter — so Inter is what this template renders and what the picker should
    // say. Saving then writes the stack explicitly, which changes the stored
    // value but not a single rendered pixel.
    if (!stack) return "inter";
    const match = Object.entries(FONT_STACKS).find(([, value]) => value === stack);
    return match ? match[0] : "custom";
}

const hexOrEmpty = z.union([z.literal(""), z.string().regex(HEX, "Use #rrggbb")]);

export const templateSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
    backgroundColor: z.string().regex(HEX, "Use #rrggbb"),
    // "" is "no image": the picker's own empty state, and what `toBody` omits.
    backgroundImageAssetId: z.string(),
    logoAssetId: z.string(),
    logoPosition: z.enum(["left", "center", "right"]),
    fontChoice: z.string(),
    customFontStack: z.string(),
    slotText: hexOrEmpty,
    slotAccent: hexOrEmpty,
    customCss: z.string(),
});

export type TemplateValues = z.infer<typeof templateSchema>;

/** A blank template, for the create page. Matches the display page's own
 *  fallbacks so the preview opens on what an unstyled screen already looks
 *  like, rather than on something the product never shows. */
export const NEW_TEMPLATE_DEFAULTS: TemplateValues = {
    name: "",
    // display.rs's design::BACKGROUND_COLOR.
    backgroundColor: "#101014",
    backgroundImageAssetId: "",
    logoAssetId: "",
    logoPosition: "center",
    fontChoice: "inter",
    customFontStack: "",
    slotText: "",
    slotAccent: "",
    customCss: TEMPLATE_CSS_SCAFFOLD,
};

export function toDefaults(template: Template): TemplateValues {
    return {
        name: template.name,
        backgroundColor: template.backgroundColor,
        backgroundImageAssetId: template.backgroundImageAssetId ?? "",
        logoAssetId: template.logoAssetId ?? "",
        // The wire type carries `logoPosition` as a plain `string` (any server
        // value round-trips even if the form's own enum narrows); the enum
        // below is this editor's contract, not the API's.
        logoPosition: template.logoPosition as TemplateValues["logoPosition"],
        fontChoice: fontChoiceFor(template.fontStack),
        customFontStack: fontChoiceFor(template.fontStack) === "custom" ? (template.fontStack ?? "") : "",
        slotText: typeof template.colorSlots.text === "string" ? template.colorSlots.text : "",
        slotAccent: typeof template.colorSlots.accent === "string" ? template.colorSlots.accent : "",
        // Empty shows the commented cheat-sheet scaffold; it only persists
        // once actually edited.
        customCss: template.customCss || TEMPLATE_CSS_SCAFFOLD,
    };
}

/** The chosen stack. Null only when "Custom…" is selected and left empty. */
function chosenFontStack(values: TemplateValues): string | null {
    if (values.fontChoice === "custom") return values.customFontStack.trim() || null;
    return FONT_STACKS[values.fontChoice] ?? null;
}

function editedCss(values: TemplateValues): string {
    return isPristineCss(values.customCss, TEMPLATE_CSS_SCAFFOLD) ? "" : values.customCss;
}

export function toBody(values: TemplateValues): TemplateBody {
    const colorSlots: Record<string, string> = {};
    if (values.slotText !== "") colorSlots.text = values.slotText;
    if (values.slotAccent !== "") colorSlots.accent = values.slotAccent;
    return {
        name: values.name,
        backgroundColor: values.backgroundColor,
        logoPosition: values.logoPosition,
        backgroundImageAssetId: values.backgroundImageAssetId || null,
        logoAssetId: values.logoAssetId || null,
        fontStack: chosenFontStack(values),
        colorSlots,
        customCss: editedCss(values) || null,
    };
}

/** The live form values as the preview consumes them. Shares `toBody`'s
 *  mapping so the preview and the save can never disagree about what the
 *  font choice or the colour slots mean; the stored-URL fields are null
 *  because an unsaved edit has no resolved artwork yet — the preview panel
 *  resolves those from the asset ids itself. */
export function toDraftTemplate(values: TemplateValues): DraftTemplate {
    // Every field below is always populated by `toBody`; the fallbacks are
    // there because TemplateBody marks them optional for PATCH, not because
    // one can actually be missing here.
    const body = toBody(values);
    return {
        backgroundColor: body.backgroundColor ?? values.backgroundColor,
        logoPosition: body.logoPosition ?? values.logoPosition,
        fontStack: body.fontStack ?? null,
        colorSlots: body.colorSlots ?? {},
        customCss: body.customCss ?? null,
        backgroundImageAssetId: body.backgroundImageAssetId ?? null,
        logoAssetId: body.logoAssetId ?? null,
        fontAssetId: null,
        backgroundImageUrl: null,
        logoUrl: null,
        fontFaceUrl: null,
    };
}
